import { IconButton, Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { CloseCircleOutline, Recognition, RecognitionCard } from '@aslanpay/portal-common-lib';
import { useCallback, useState } from 'react';

import useRecognition from 'app/hooks/useRecognition';

interface PotRecognitionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  recognition: Recognition;
}

function PotRecognitionDetailsModal({
  isOpen,
  onClose,
  recognition,
}: PotRecognitionDetailsModalProps) {
  const { mutate: hideRecognition } = useRecognition.useHideRecognition();
  const { mutate: unhideRecognition } = useRecognition.useUnhideRecognition();
  const { mutate: addReaction } = useRecognition.useAddReaction();
  const { mutate: deleteReaction } = useRecognition.useDeleteReaction();

  const [pendingHideId, setPendingHideId] = useState<string | null>(null);
  const [pendingReactId, setPendingReactId] = useState<string | null>(null);

  const handleHide = useCallback(
    (id: string) => {
      setPendingHideId(id);
      hideRecognition(id, { onSettled: () => setPendingHideId(null) });
    },
    [hideRecognition]
  );

  const handleUnhide = useCallback(
    (id: string) => {
      setPendingHideId(id);
      unhideRecognition(id, { onSettled: () => setPendingHideId(null) });
    },
    [unhideRecognition]
  );

  const handleReact = useCallback(
    (id: string) => {
      setPendingReactId(id);
      addReaction(
        { recognitionId: id, body: { reactionType: 'ROAR' } },
        { onSettled: () => setPendingReactId(null) }
      );
    },
    [addReaction]
  );

  const handleUnreact = useCallback(
    (id: string) => {
      setPendingReactId(id);
      deleteReaction(
        { recognitionId: id, reactionType: 'ROAR' },
        { onSettled: () => setPendingReactId(null) }
      );
    },
    [deleteReaction]
  );

  if (!recognition) return null;

  const visibleRecognition = { ...recognition, adminHidden: false };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={4}
        sx={{
          width: '420px !important',
          maxWidth: '420px !important',
          minWidth: 'unset !important',
        }}
      >
        <IconButton
          variant="link"
          icon={<CloseCircleOutline boxSize="30px" />}
          aria-label="Close modal"
          onClick={onClose}
          position="absolute"
          top="16px"
          right="8px"
          zIndex={1}
          minW="auto"
          color="brand.purple"
        />
        <ModalBody p={0}>
          <RecognitionCard
            recognition={visibleRecognition}
            variant="grid"
            canModerate={false}
            onHide={handleHide}
            onUnhide={handleUnhide}
            onReact={handleReact}
            onUnreact={handleUnreact}
            isHidePending={pendingHideId === recognition.id}
            isReactPending={pendingReactId === recognition.id}
            fitHeight
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default PotRecognitionDetailsModal;
